import { createClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl) {
  throw new Error(
    "Missing VITE_SUPABASE_URL environment variable. " +
      "Set it in your .env file or Doppler config.",
  );
}

if (!supabaseAnonKey) {
  throw new Error(
    "Missing VITE_SUPABASE_ANON_KEY environment variable. " +
      "Set it in your .env file or Doppler config.",
  );
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

export interface EdgeFunctionErrorOptions {
  code?: string;
  status?: number;
  correlationId?: string;
  requestId?: string;
}

export class EdgeFunctionError extends Error {
  code?: string;
  status?: number;
  correlationId?: string;
  requestId?: string;

  constructor(message: string, options: EdgeFunctionErrorOptions = {}) {
    super(message);
    this.name = "EdgeFunctionError";
    this.code = options.code;
    this.status = options.status;
    this.correlationId = options.correlationId;
    this.requestId = options.requestId;
  }
}

/** Generic fetch-failure message from the Supabase SDK (FunctionsFetchError) */
const GENERIC_EDGE_FN_MSG = "Failed to send a request to the Edge Function";

/**
 * Check whether an edge-function error is a network-level failure
 * (FunctionsFetchError — the function is unreachable, not an HTTP error).
 */
export function isEdgeFunctionUnreachable(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const msg =
    error instanceof Error
      ? error.message
      : "message" in error && typeof (error as { message: unknown }).message === "string"
        ? (error as { message: string }).message
        : "";
  return msg === GENERIC_EDGE_FN_MSG;
}

/** User-friendly fallback when the edge function is unreachable */
const USER_FRIENDLY_EDGE_FN_MSG =
  "Unable to reach the server. Please check your connection and try again.";

/**
 * Extract the real error message from a Supabase edge function response.
 * FunctionsHttpError wraps the Response in .context with a generic message —
 * this reads the actual JSON error body from the response.
 *
 * FunctionsFetchError (network failure) has no Response to read, so we replace
 * the raw SDK message with a user-friendly one.
 */
export async function extractFunctionsError(error: unknown): Promise<Error> {
  if (
    error &&
    typeof error === "object" &&
    "context" in error &&
    (error as { context: unknown }).context instanceof Response
  ) {
    try {
      const body = await (error as { context: Response }).context.json();
      if (body?.error) {
        const response = (error as { context: Response }).context;
        return new EdgeFunctionError(body.error, {
          code: typeof body.code === "string" ? body.code : undefined,
          status: response.status,
          correlationId: typeof body.correlationId === "string" ? body.correlationId : undefined,
          requestId: typeof body.request_id === "string" ? body.request_id : undefined,
        });
      }
    } catch {
      // Response body couldn't be parsed — fall through to original error
    }
  }

  // Replace the generic SDK message with something user-friendly
  const msg =
    error instanceof Error
      ? error.message
      : error &&
          typeof error === "object" &&
          "message" in error &&
          typeof (error as { message: unknown }).message === "string"
        ? (error as { message: string }).message
        : String(error);

  if (msg === GENERIC_EDGE_FN_MSG) {
    return new EdgeFunctionError(USER_FRIENDLY_EDGE_FN_MSG, {
      code: "EDGE_FUNCTION_UNREACHABLE",
    });
  }

  return new EdgeFunctionError(msg);
}
